import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {RGBA} from '@Utils/color.util';
import {getRgbaArrayFromFabricColor, getColorStringsFromFabricColor} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import type {CopyableItemStyles, TFabricColor, TSerializedFabricColor} from '@PosterWhiteboard/items/item/item.types';
import {type FabricGradientObject} from '@PosterWhiteboard/items/item/item.types';
import {type InitItemOpts, ITEM_TYPE, type ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {cloneAsImageWithAngleIgnored, getPaths} from '@Utils/fabric.util';
import type {VectorItemObject} from '@PosterWhiteboard/items/vector-item/vector-item.types';
import type {VectorItemSource} from '@PosterWhiteboard/items/vector-item/vector-item.library';
import {arePathsCustomizable, loadVectorSVG} from '@PosterWhiteboard/items/vector-item/vector-item.library';
import type {ColorStop, FabricObject, Path} from '@postermywall/fabricjs-2';
import {Gradient} from '@postermywall/fabricjs-2';
import {util, Group} from '@postermywall/fabricjs-2';
import type {DeepPartial} from '@/global';
import isObject from 'lodash/isObject';

const MAX_VECTOR_PATHS = 200;

export class VectorItem extends Item {
  declare _fabricObject: Group | Path;

  public gitype = ITEM_TYPE.VECTOR;
  public source!: VectorItemSource;
  public fileName = '';

  public isCustomisable = true;

  /**
   * Treat the vector as single-fill even when its SVG paths have differing colors: every path is painted with
   * the item's fill. Persisted so a flattened shape stays flattened (and recolorable) when the design reloads.
   */
  public forceUniformFill = false;

  override get fabricObject(): Group | Path {
    return this._fabricObject;
  }

  public override toObject(): VectorItemObject {
    return {
      ...super.toObject(),
      fileName: this.fileName,
      source: this.source,
      isCustomisable: this.isCustomisable,
      forceUniformFill: this.forceUniformFill,
    };
  }

  protected override async onItemInitialized(): Promise<void> {
    if (this.hasTooManyPaths(this.fabricObject)) {
      await this.convertVectorToImage();
    }
    await this.updateFabricObject();
  }

  protected override isVisible(): boolean {
    return (this.fabricObject.fill && this.fabricObject.opacity > 0) || this.border.solidBorderThickness > 0;
  }

  protected override async getFabricObjectForItem(opts?: InitItemOpts): Promise<FabricObject> {
    const {paths} = await loadVectorSVG(this.fileName, this.source);
    const fabricOpts = {
      ...this.getInitOptionsForView(),
    };
    this.isCustomisable = this.forceUniformFill || arePathsCustomizable(paths);
    this.preparePaths(paths);

    const svg = util.groupSVGElements(paths, fabricOpts);
    svg.set({
      uniformScaling: !(this.fileName === '803' || this.fileName === '804'),
      ...this.getScalesAndShadowToMaintainScaledDimensions(svg.width, svg.height, opts),
    });
    return svg;
  }

  protected hasSinglePath(): boolean {
    return !(this.fabricObject instanceof Group);
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<VectorItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);

    if (obj.fill !== undefined && this.isCustomisable) {
      // @ts-expect-error this is never deep partial for now. Fix this later
      const fill = this.changeFillForVector(obj.fill);
      const unserializedObj = await util.enlivenObjectEnlivables({
        fill,
      });

      this.fabricObject.set('fill', unserializedObj.fill);
      this.applyFillToPaths(unserializedObj.fill as any);
    }
  }

  private changeFillForVector(fill: TSerializedFabricColor): TSerializedFabricColor {
    if (isObject(fill) && fill.type === 'radial') {
      return getRadialGradientObjectForVectorItem(getColorStringsFromFabricColor(fill), this.fabricObject.width, this.fabricObject.height);
    }
    return fill;
  }

  protected override async reInitFabricObject(): Promise<void> {
    await super.reInitFabricObject();
    this.applyFillToPaths(this.fabricObject.fill);
  }

  private applyFillToPaths(fill: TFabricColor): void {
    if (this.fabricObject instanceof Group) {
      const paths = getPaths(this.fabricObject);
      for (const item of paths) {
        item.set('fill', fill);
      }
    }
  }

  public override applyBorder(): void {
    if (this.isCustomisable) {
      const paths = getPaths(this.fabricObject);
      const strokeHex = rgbaToHexString(this.border.solidBorderColor);

      for (const item of paths) {
        item.set({
          stroke: strokeHex,
        });
      }

      this.fabricObject.set({
        ...this.border.getBorder(),
      });
    }
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    if (this.fabricObject.fill) {
      colors.push(...getRgbaArrayFromFabricColor(this.getFillObject()));
    }

    colors.push(this.border.solidBorderColor);

    return colors;
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      frontFill: this.toObject().fill,
      textBorder: this.border.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<VectorItemObject> = {};
    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.fill = copiedStyles.frontFill;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  protected override isRoundedBorderSupported(): boolean {
    return false;
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: VectorItemObject): boolean {
    if (!this.fabricObject) {
      return false;
    }

    if (this.isCustomisable && !this.hasSinglePath()) {
      if (oldItemObject.border.solidBorderThickness !== this.border.solidBorderThickness) {
        return true;
      }
    }
    return false;
  }

  protected override getInitOptionsForView(): Record<string, unknown> {
    return {
      perPixelTargetFind: true,
      selectable: true,
      lockUniScaling: false,
    };
  }

  private preparePaths(paths: FabricObject[]): void {
    if (this.isCustomisable) {
      for (const path of paths) {
        path.set({
          strokeWidth: this.border.solidBorderThickness,
          strokeUniform: true,
        });
      }
    }
  }

  private hasTooManyPaths(svg: FabricObject): boolean {
    return getPaths(svg).length > MAX_VECTOR_PATHS;
  }

  public hasMultiplePaths(): boolean {
    return getPaths(this.fabricObject).length > 1;
  }

  private async convertVectorToImage(): Promise<void> {
    this.applyBorder();
    const img = await cloneAsImageWithAngleIgnored(this.fabricObject, {
      multiplier: this.page.poster.scaling.scale,
    });
    if (!img.width || !img.height) {
      throw new Error('Failed to get image for vector');
    }

    img.set({
      scaleX: this.fabricObject.scaleX * (this.fabricObject.get('width') / img.width),
      scaleY: this.fabricObject.scaleY * (this.fabricObject.get('height') / img.height),
    });
    this.setFabricObject(img);
    this.isCustomisable = false;
  }
}

const getRadialGradientObjectForVectorItem = (colors: string[], width: number, height: number): FabricGradientObject<'radial'> => {
  const x = width / 2;
  const y = height / 2;
  const maxDimension = Math.max(x, y);

  const colorStops: ColorStop[] = [];
  for (let i = 0; i < colors.length; i++) {
    colorStops.push({
      offset: Math.round(i / colors.length) * 0.75, // to cater for most SVGs having space between the control box and shape
      color: colors[i],
    });
  }

  return new Gradient<'radial', 'radial'>({
    colorStops: colorStops,
    type: 'radial',
    coords: {
      r1: 0.1 * maxDimension * 2,
      r2: maxDimension * 2,
      x1: maxDimension,
      y1: maxDimension,
      x2: maxDimension,
      y2: maxDimension,
    },
  }).toObject() as FabricGradientObject<'radial'>;
};
